<script setup>
import { computed, ref, watch } from 'vue'
import { APPEARANCE_FIELDS, APPEARANCE_DEFAULTS, APPEARANCE_PRESETS, readAppearance, updateAppearance, isAppearanceColor, resolveAppearanceColors, colorContrast } from '../../../../../shared/commerceAppearance.js'
import { commerceSettingsApi } from '@/api/modules/commerceSettings.js'
import { commerceAppearancePreview, APPEARANCE_PREVIEW_PAGES } from '@/utils/commerceAppearancePreview.js'
import mallBanner from '../../../../mall/public/images/banner.png'

const props = defineProps({ modelValue: Object, displayName: String })
const emit = defineEmits(['update:modelValue'])
const colors = ref({ ...APPEARANCE_DEFAULTS }), applyAll = ref(false), saving = ref(false), error = ref(''), saved = ref(false)
const page = ref('home'), device = ref('desktop')
watch(() => props.modelValue, value => { colors.value = { ...readAppearance(value).colors }; applyAll.value = false }, { immediate: true })
const valid = computed(() => APPEARANCE_FIELDS.every(field => isAppearanceColor(colors.value[field.key])))
const previewAppearance = computed(() => {
  const current = readAppearance(props.modelValue)
  if (!valid.value) return current
  return updateAppearance(current, { colors: colors.value, revision: current.revision, applyAll: applyAll.value })
})
const previewDocument = computed(() => commerceAppearancePreview(previewAppearance.value, page.value, mallBanner, props.displayName))
const checks = computed(() => {
  const c = resolveAppearanceColors(previewAppearance.value)
  return [['搜尋按鈕', c.searchText, c.searchBackground], ['次要按鈕滑入', c.onSecondary, c.secondaryHover], ['主要按鈕', c.onAction, c.action], ['主選單', c.onNav, c.nav], ['頂部資訊列', c.topText, c.topBackground], ['頁尾資訊區', c.footerText, c.footerBackground]]
    .map(([label, text, background]) => ({ label, ratio: colorContrast(text, background) }))
})
function setColor(key, value) { colors.value[key] = value; saved.value = false; error.value = '' }
function presetColors(preset) { colors.value = { ...preset.colors }; applyAll.value = true; saved.value = false }
function resetAll() { colors.value = { ...APPEARANCE_DEFAULTS }; applyAll.value = true; saved.value = false }
async function save() {
  if (!valid.value || saving.value) return
  saving.value = true; error.value = ''; saved.value = false
  try {
    const current = readAppearance(props.modelValue)
    const response = await commerceSettingsApi.updateSettings({ storefrontAppearance: { colors: colors.value, revision: current.revision, applyAll: applyAll.value } })
    emit('update:modelValue', readAppearance(response.data.storefrontAppearance))
    saved.value = true
  } catch (e) { error.value = e.message || '儲存失敗，請稍後再試' }
  finally { saving.value = false }
}
</script>

<template>
  <section class="settings-card appearance-editor" aria-label="商城外觀色系設定">
    <div class="settings-card-header settings-card-header--with-actions">
      <i class="ri-paint-brush-line" style="color: #667eea;" aria-hidden="true"></i>
      <div class="settings-card-header__main">
        <h2 class="settings-card-title">商城外觀色系</h2>
        <p class="settings-card-desc">共 10 項配色，讓相同色系的區域一起更新。</p>
      </div>
      <div class="settings-card-header__actions">
        <span v-if="saved" role="status" class="save-feedback save-feedback--ok">已儲存，前台立即生效</span>
        <button type="button" class="btn btn-light" :disabled="saving" @click="resetAll">恢復預設</button>
        <button type="button" class="btn btn-primary" :disabled="saving || !valid" @click="save">
          {{ saving ? '儲存中…' : '儲存' }}
        </button>
      </div>
    </div>
    <div class="form-body">
      <p v-if="error" class="appearance-error" role="alert">{{ error }}</p>
      <p v-if="!valid" class="appearance-error" role="alert">請使用完整的 #RRGGBB 色碼。</p>
      <div class="appearance-note">先在右側預覽，儲存後套用至前台。商城版型與既有提示框維持不變。</div>
      <div class="appearance-columns">
        <section class="appearance-controls">
          <div class="section-heading">
            <h3>外觀設定</h3>
            <span>10 項配色</span>
          </div>
          <div class="appearance-presets">
            <button
              v-for="preset in APPEARANCE_PRESETS"
              :key="preset.id"
              type="button"
              :disabled="saving"
              @click="presetColors(preset)"
            >
              <i>
                <b
                  v-for="key in ['brand', 'secondary', 'action']"
                  :key="key"
                  :style="{ background: preset.colors[key] }"
                />
              </i>
              {{ preset.label }}
            </button>
          </div>
          <div
            v-for="(field, index) in APPEARANCE_FIELDS"
            :key="field.key"
            class="color-field"
            :class="{ 'color-field-divider': index === 6 }"
          >
            <h4 v-if="index === 0 || index === 6">
              {{ index === 0 ? '品牌與重點色' : '頂部與頁尾資訊區' }}
            </h4>
            <div class="color-row">
              <div class="color-label">
                <label :for="`appearance-${field.key}`">{{ field.label }}</label>
                <small>{{ field.description }}</small>
              </div>
              <div class="color-inputs">
                <input
                  type="color"
                  :aria-label="`${field.label}色票`"
                  :value="isAppearanceColor(colors[field.key]) ? colors[field.key] : field.default"
                  :disabled="saving"
                  @input="setColor(field.key, $event.target.value.toUpperCase())"
                >
                <input
                  :id="`appearance-${field.key}`"
                  :aria-label="`${field.label}色碼`"
                  :value="colors[field.key]"
                  maxlength="7"
                  pattern="#[0-9a-fA-F]{6}"
                  :disabled="saving"
                  @input="setColor(field.key, $event.target.value)"
                >
                <button
                  type="button"
                  :aria-label="`重設${field.label}`"
                  :disabled="saving"
                  @click="setColor(field.key, field.default)"
                >
                  <i class="ri-refresh-line" aria-hidden="true"></i>
                </button>
              </div>
            </div>
          </div>
          <p class="automatic-note">
            可單獨調整各項顏色，也可選擇配色方案一次套用全部 10 色。頁面背景、一般文字與框線沿用原切版；儲存後生效。
          </p>
        </section>
        <div class="appearance-preview">
          <div class="section-heading">
            <h3>商城即時預覽</h3>
            <div class="device-buttons">
              <button type="button" :class="{ selected: device === 'desktop' }" :aria-pressed="device === 'desktop'" @click="device = 'desktop'">桌機</button>
              <button type="button" :class="{ selected: device === 'mobile' }" :aria-pressed="device === 'mobile'" @click="device = 'mobile'">手機</button>
            </div>
          </div>
          <nav aria-label="外觀預覽頁面">
            <button
              v-for="[key, label] in APPEARANCE_PREVIEW_PAGES"
              :key="key"
              type="button"
              :class="{ selected: page === key }"
              :aria-pressed="page === key"
              @click="page = key"
            >
              {{ label }}
            </button>
          </nav>
          <div class="preview-stage">
            <iframe title="商城外觀預覽" :class="{ mobile: device === 'mobile' }" :srcdoc="previewDocument" sandbox="" tabindex="-1"></iframe>
          </div>
          <p class="preview-caption">匿名商品與交易資料示意；Banner 圖片不受色票影響。</p>
          <div class="contrast-checks">
            <strong>文字可讀性檢查</strong>
            <small>一般文字建議 ≥ 4.5 : 1</small>
            <p v-for="check in checks" :key="check.label">
              <span>{{ check.label }}</span>
              <span :class="check.ratio >= 4.5 ? 'pass' : 'warn'">
                {{ check.ratio.toFixed(2) }} : 1 · {{ check.ratio >= 4.5 ? '可讀性良好' : '建議調整' }}
              </span>
            </p>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
/* 對齊商城設定其他分頁的 settings-card 標題列 */
/* 貼齊 Tab 列，與選單／Footer／收貨時段等分頁一致 */
.settings-card {
  background: #fff;
  border: 1px solid #e9ecef;
  border-top: none;
  border-radius: 0 0 12px 12px;
  overflow: hidden;
  color: #263044;
}

.settings-card-header {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 24px;
  background: #f8f9ff;
  border-bottom: 1px solid #e9ecef;
}

.settings-card-header > i {
  font-size: 32px;
  margin-top: 2px;
  flex-shrink: 0;
}

.settings-card-header--with-actions {
  align-items: center;
}

.settings-card-header__main {
  flex: 1;
  min-width: 0;
}

.settings-card-header__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  flex-shrink: 0;
  margin-left: auto;
}

.settings-card-title {
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 4px;
  color: #2d3748;
}

.settings-card-desc {
  font-size: 13px;
  color: #6c757d;
  margin: 0;
}

.form-body {
  padding: 16px 24px 24px;
}

.save-feedback--ok {
  font-size: 12px;
  color: #059669;
}

.appearance-note {
  padding: 12px 16px;
  background: #efedf9;
  border: 1px solid #e5e1f5;
  border-radius: 7px;
  color: #756992;
  font-size: 12px;
  margin-bottom: 18px;
}

.appearance-error {
  color: #b5202d;
  font-size: 13px;
  margin: 0 0 12px;
}

.appearance-columns {
  display: grid;
  grid-template-columns: 480px minmax(0, 1fr);
  gap: 20px;
  align-items: start;
}

.appearance-controls,
.appearance-preview {
  background: white;
  border: 1px solid #e6e8f0;
  border-radius: 9px;
  overflow: hidden;
}

.section-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px;
  border-bottom: 1px solid #e6e8f0;
}

.section-heading h3 {
  margin: 0;
  font-size: 16px;
}

.section-heading > span {
  background: #f0edff;
  color: #7465cc;
  border-radius: 4px;
  font-size: 14px;
  padding: 5px 7px;
}

.appearance-presets {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 7px;
  padding: 18px 18px 5px;
}

.appearance-presets button {
  padding: 7px 4px;
  background: white;
  border: 1px solid #e6e8f0;
  border-radius: 5px;
  font-size: 14px;
  color: #657085;
}

.appearance-presets i {
  display: flex;
  height: 25px;
  overflow: hidden;
  border-radius: 3px;
  margin-bottom: 7px;
}

.appearance-presets b {
  flex: 1;
}

.color-field {
  padding: 10px 18px;
}

.color-field h4 {
  font-size: 16px;
  margin: 9px 0 17px;
}

.color-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 178px;
  align-items: center;
  gap: 16px;
}

.color-label {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.color-label label,
.color-label small {
  overflow-wrap: anywhere;
}

.color-row label {
  font-size: 14px;
  font-weight: 600;
  margin: 0;
}

.color-inputs {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.color-inputs > * {
  box-sizing: border-box;
  flex-shrink: 0;
}

.color-inputs input[type='color'] {
  width: 40px;
  height: 36px;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 2px;
  background: none;
  cursor: pointer;
}

.color-inputs input:not([type='color']) {
  width: 90px;
  height: 32px;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 7px 10px;
  font: 14px monospace;
  color: #495057;
  text-transform: uppercase;
}

.color-inputs input:invalid {
  border-color: #b5202d;
}

.color-inputs button {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #dee2e6;
  background: #fff;
  border-radius: 6px;
  color: #6c757d;
  font-size: 14px;
  padding: 0;
  cursor: pointer;
  transition: all 0.15s;
}

.color-inputs button:hover {
  background: #f8f9ff;
  border-color: #667eea;
  color: #667eea;
}

.color-inputs input:not([type='color']):focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.12);
}

.color-field small {
  display: block;
  font-size: 12px;
  color: #8992a2;
  line-height: 1.6;
  margin-top: 0;
}

.color-field-divider {
  border-top: 1px solid #e6e8f0;
  margin-top: 12px;
  padding-top: 15px;
}

.automatic-note {
  font-size: 14px;
  color: #817797;
  background: #f5f2fb;
  border-radius: 6px;
  padding: 12px;
  margin: 14px 18px 18px;
  line-height: 1.8;
}

.appearance-preview {
  position: sticky;
  top: 20px;
}

.device-buttons {
  display: flex;
  gap: 4px;
}

.device-buttons button,
.appearance-preview nav button {
  border: 0;
  background: transparent;
  font-size: 14px;
  color: #657085;
  padding: 6px 9px;
  border-radius: 5px;
}

.appearance-preview button.selected {
  background: #efecfb;
  color: #6b5dca;
}

.appearance-preview nav {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  padding: 10px 12px;
  border-bottom: 1px solid #e6e8f0;
}

.preview-stage {
  padding: 18px;
  background: #eceef3;
}

iframe {
  display: block;
  width: 100%;
  height: 560px;
  background: white;
  border: 0;
  border-radius: 4px;
  margin: auto;
}

iframe.mobile {
  max-width: 375px;
  border: 7px solid #252b38;
  border-radius: 22px;
}

.preview-caption {
  font-size: 14px;
  color: #8b93a3;
  margin: 10px 16px;
}

.contrast-checks {
  border-top: 1px solid #e6e8f0;
  padding: 14px 18px;
  font-size: 14px;
}

.contrast-checks > small {
  float: right;
  color: #8b93a3;
  font-size: 14px;
}

.contrast-checks p {
  display: flex;
  justify-content: space-between;
  margin: 8px 0 0;
  color: #7c8492;
}

.pass {
  color: #34876c;
}

.warn {
  color: #b87935;
}

@media (max-width: 1200px) {
  .appearance-columns {
    grid-template-columns: 450px minmax(0, 1fr);
    gap: 14px;
  }

  .color-field {
    padding-left: 13px;
    padding-right: 13px;
  }
}

@media (max-width: 950px) {
  .appearance-columns {
    grid-template-columns: 1fr;
  }

  .appearance-preview {
    position: static;
  }

  .settings-card-header--with-actions {
    align-items: flex-start;
    flex-wrap: wrap;
  }
}

@media (max-width: 500px) {
  .color-row {
    grid-template-columns: minmax(0, 1fr);
    gap: 8px;
  }

  .color-inputs {
    justify-self: end;
  }

  .settings-card-header__actions {
    width: 100%;
  }

  .preview-stage {
    padding: 10px;
  }

  .contrast-checks > small {
    float: none;
    display: block;
    margin-top: 6px;
  }
}
</style>
